
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 4,
  layout: 'reference',
  secondLevelToc: true,
  alwaysExpand: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'API reference for endpoints and webhooks associated with the Plaid Statements product.',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Statements' subtitle='API reference for Statements endpoints and webhooks' compact mdxType="Header" />
    <p>{`For how-to guidance, see the `}<a parentName="p" {...{
        "href": "/docs/statements/"
      }}>{`Statements documentation`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoint`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/statements/list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get a list of statements available to download`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/statements/download`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Download a single bank statement`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/statements/refresh`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Trigger on-demand statement extractions`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Webhook Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#statements_refresh_complete"
            }}><inlineCode parentName="a">{`STATEMENTS_REFRESH_COMPLETE`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Statements refresh completed`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "statementslist"
    }}>{`/statements/list`}</h4>
    <Schema method='POST' route='/statements/list' type='request' mdxType="Schema">
      <MDXCodeBlock title='/statements/list' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = StatementsListRequest(access_token=access_token)
response = client.statements_list(request)
accounts = response['accounts']
statements = accounts[0]['statements']
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/statements/list \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": String
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`StatementsListRequest statementsListRequest = new StatementsListRequest().accessToken(accessToken);
Response<StatementsListResponse> statementsListResponse = client.statementsList(statementsListRequest).execute();
List<StatementsAccount> accounts = statementsListResponse.body().getAccounts();
List<StatementsStatement> statements = accounts.get(0).getStatements();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`statements_list_request = Plaid::StatementsListRequest.new
statements_list_request.access_token = access_token
response = client.statements_list(statements_list_request)
accounts = response.accounts
statements = accounts[0].statements
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const listRequest: StatementsListRequest = {
  access_token: access_token,
};
const listResponse = await plaidClient.statementsList(listRequest);
accounts = listResponse.data.accounts;
statements = listResponse.data.accounts[0].statements;
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`statementsListRequest := plaid.NewStatementsListRequest(accessToken)
statementsListResponse, _, err := client.PlaidApi.StatementsList(ctx).StatementsListRequest(*statementsListRequest).Execute()
accounts := statementsListResponse.GetAccounts()
statements := accounts[0].GetStatements()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`statementsListRequest := &plaid.StatementsListRequest{AccessToken: accessToken}
statementsListResponse, err := client.Statements.List(ctx, statementsListRequest)
accounts := statementsListResponse.GetAccounts()
statements := accounts[0].GetStatements()
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/statements/list' type='response' mdxType="Schema" />
    <h4 {...{
      "id": "statementsdownload"
    }}>{`/statements/download`}</h4>
    <Schema method='POST' route='/statements/download' type='request' mdxType="Schema">
      <MDXCodeBlock title='/statements/download' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = StatementsDownloadRequest(
  access_token=access_token,
  statement_id=statement_id,
)
pdf, status_code, headers = client.statements_download(request, _return_http_data_only=False)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/statements/download \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": String,
   "statement_id": String
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`StatementsDownloadRequest statementsDownloadRequest = new StatementsDownloadRequest().accessToken(accessToken).statementId(statementID);
Response<ResponseBody> response = client.statementsDownload(statementsDownloadRequest).execute();
byte[] pdf = response.body().bytes();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`statements_download_request = Plaid::StatementsDownloadRequest.new
statements_download_request.access_token = access_token
statements_download_request.statement_id = statement.statement_id
pdf, status_code, headers = client.statements_download_with_http_info(statements_download_request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`let downloadRequest: StatementsDownloadRequest = {
  access_token: accessToken,
  statement_id: statement.statement_id,
};
let downloadResponse = await plaidClient.statementsDownload(
  downloadRequest,
  {responseType: 'arraybuffer'},
);
let pdf = downloadResponse.data.toString('base64');
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`downloadRequest := plaid.NewStatementsDownloadRequest(accessToken, statement.GetStatementId())
_, downloadResponse, err := client.PlaidApi.StatementsDownload(ctx).StatementsDownloadRequest(*downloadRequest).Execute()
if err != nil {
  return err
}
pdf, err := io.ReadAll(downloadResponse.Body)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`downloadRequest := &plaid.StatementsDownloadRequest{
    AccessToken: accessToken,
    StatementID: statement.GetStatementID(),
}
downloadResponse, err := client.Statements.Download(ctx, downloadRequest)
if err != nil {
    return err
}
pdf, err := io.ReadAll(downloadResponse)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h5 {...{
      "id": "response"
    }}>{`Response`}</h5>
    <p>{`This endpoint returns a single statement, exactly as provided by the financial institution, in the form of binary PDF data.`}</p>
    <h4 {...{
      "id": "statementsrefresh"
    }}>{`/statements/refresh`}</h4>
    <Schema method='POST' route='/statements/refresh' type='request' mdxType="Schema">
      <MDXCodeBlock title='/statements/refresh' type='server' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`request = StatementsRefreshRequest(
    access_token=access_token,
    start_date=date.today() - timedelta(days=90),
    end_date=date.today(),
)
response = client.statements_refresh(request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/statements/refresh \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "access_token": String,
   "start_date": "2023-08-01",
   "end_date": "2023-11-01"
 }'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`StatementsRefreshRequest statementsRefreshRequest = new StatementsRefreshRequest().accessToken(accessToken).startDate(LocalDate.parse(startDate)).endDate(LocalDate.parse(endDate));
Response<StatementsRefreshResponse> response = client.statementsRefresh(statementsRefreshRequest).execute();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`statements_refresh_request = Plaid::StatementsRefreshRequest.new
statements_refresh_request.access_token = access_token
statements_refresh_request.start_date = Date.today.prev_year
statements_refresh_request.end_date = Date.today.prev_month(10)
response = client.statements_refresh(statements_refresh_request)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`const refreshRequest: StatementsRefreshRequest = {
  access_token: accessToken,
  start_date: '2023-11-01',
  end_date: '2024-02-01',
};
const refreshResponse = await plaidClient.statementsRefresh(refreshRequest);
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`startDate, endDate := "2023-11-01", "2024-01-01"
refreshRequest := plaid.NewStatementsRefreshRequest(accessToken, startDate, endDate)
refreshResponse, _, err := client.PlaidApi.StatementsRefresh(ctx).StatementsRefreshRequest(*refreshRequest).Execute()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`startDate, endDate := time.Date(2023, 11, 1, 0, 0, 0, 0, time.UTC), time.Date(2024, 1, 1, 0, 0, 0, 0, time.UTC)
refreshRequest := &plaid.StatementsRefreshRequest{
    AccessToken: accessToken,
    StartDate:   startDate,
    EndDate:     endDate,
}
refreshResponse, err := client.Statements.Refresh(ctx, refreshRequest)
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/statements/refresh' type='response' mdxType="Schema" />
    <h3 {...{
      "id": "webhooks"
    }}>{`Webhooks`}</h3>
    <p>{`Statement webhooks are sent to indicate that statements refresh has finished processing. All webhooks related to statements have a `}<inlineCode parentName="p">{`webhook_type`}</inlineCode>{` of `}<inlineCode parentName="p">{`STATEMENTS`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "statements_refresh_complete"
    }}><inlineCode parentName="h4">{`STATEMENTS_REFRESH_COMPLETE`}</inlineCode></h4>
    <Schema type='model' model='StatementsRefreshCompleteWebhook' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "endpoints",
        level: 3,
        title: "Endpoints",
      },
{
        id: "statementslist",
        level: 4,
        title: "/statements/list",
      },
{
        id: "statementsdownload",
        level: 4,
        title: "/statements/download",
      },
{
        id: "statementsrefresh",
        level: 4,
        title: "/statements/refresh",
      },
{
        id: "webhooks",
        level: 3,
        title: "Webhooks",
      },
{
        id: "statements_refresh_complete",
        level: 4,
        title: "STATEMENTS_REFRESH_COMPLETE",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
